import type { RespondentRule } from "./respondent-rule";

/**
 * The identity rule: when a rule needs to know who someone is, they sign in with the login we already
 * have before they answer. Screens and the server both read it from here, so they cannot drift.
 */

/** Why a person must sign in before answering. Sent as the `reason` param on the sign-in redirect. */
export const IDENTITY_REASONS = ["one_per_person", "invited", "login", "sso", "domain"] as const;

/** One reason a rule needs identity. */
export type IdentityReason = (typeof IDENTITY_REASONS)[number];

/**
 * Whether a rule needs a verified identity: it is not public, or it allows one response per person,
 * limits email domains, or names an SSO domain. A hotel link needs one when it is invited.
 *
 * @param rule - The respondent rule.
 * @returns True when the respondent must be signed in with a verified email.
 * @throws Never.
 */
export function needsIdentity(rule: RespondentRule): boolean {
  return identityReason(rule) !== null;
}

/**
 * The reason a rule needs identity, the most specific one first: the access mode, then allowed
 * domains, then one response per person.
 *
 * @param rule - The respondent rule.
 * @returns The reason, or null when anyone may answer without signing in.
 * @throws Never.
 */
export function identityReason(rule: RespondentRule): IdentityReason | null {
  if (rule.mode !== "public") return rule.mode;
  if (rule.allowedDomains.length > 0) return "domain";
  if (rule.ssoDomain) return "sso";
  if (rule.onePerPerson) return "one_per_person";
  return null;
}

/**
 * Whether a value (a `reason` query param) is a known identity reason, so a page never renders one it
 * does not know.
 *
 * @param value - The raw value.
 * @returns True for one of {@link IDENTITY_REASONS}.
 * @throws Never.
 */
export function isIdentityReason(value: unknown): value is IdentityReason {
  return typeof value === "string" && (IDENTITY_REASONS as readonly string[]).includes(value);
}
